import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import UiIcon from "../components/UiIcon";
import { cachedRequest, invalidateCached } from "./api";
import { executiveCopy } from "./executive-copy";
import ExecutiveOrgStructure from "./ExecutiveOrgStructure";
import { formatNumber } from "./copy";
import { localizePeriodLabel } from "../i18n/period-labels";
import { withPercent } from "../i18n/number-format";
import {
    buildAttentionItems, buildExecutiveTrend, comparisonReliable, delta, indicatorCounts, performanceDistribution, periodState,
    previousFinalized, rankIndicators, summarizePeople, topLevelUnits, unitBreakdown,
} from "./executive-insights";

const MANAGED = { "data-cvi-i18n": "managed" };
const EXECUTIVE_PATH = periodId => `/api/cvi/periods/${encodeURIComponent(periodId)}/executive`;

function viewerKey() {
    try { return JSON.parse(sessionStorage.getItem("cvi_user") || "null")?.id || ""; } catch { return ""; }
}

// Loads the executive result of every started period. Locked periods are cheap snapshot reads;
// the active period is the live server calculation. Each period fails independently.
export function useExecutivePeriods(periods) {
    const ids = periods.map(period => period.id).join("|");
    const periodsRef = useRef(periods);
    periodsRef.current = periods;
    const [state, setState] = useState({});
    const [reloadKey, setReloadKey] = useState(0);
    useEffect(() => {
        let cancelled = false;
        const viewer = viewerKey();
        const periods = periodsRef.current;
        setState(prev => Object.fromEntries(periods.map(period => [period.id, { ...(prev[period.id] || {}), loading: true }])));
        periods.forEach(period => {
            const path = EXECUTIVE_PATH(period.id);
            cachedRequest(path, { key: `${path}|${viewer}` })
                .then(payload => { if (!cancelled) setState(prev => ({ ...prev, [period.id]: { payload, error: null, loading: false } })); })
                .catch(error => { if (!cancelled) setState(prev => ({ ...prev, [period.id]: { payload: null, error, loading: false } })); });
        });
        return () => { cancelled = true; };
    }, [ids, reloadKey]);
    const reload = useCallback(() => {
        invalidateCached("/api/cvi/periods/");
        setReloadKey(key => key + 1);
    }, []);
    return { byPeriod: state, reload };
}

const fmt = (value, language, digits = 1) => formatNumber(value, language, { minimumFractionDigits: digits, maximumFractionDigits: digits });
const fmtSigned = (value, language) => (value == null ? "—" : `${value > 0 ? "+" : value < 0 ? "−" : "±"}${fmt(Math.abs(value), language)}`);

function DeltaChip({ value, language, title }) {
    if (value == null) return null;
    const tone = Math.abs(value) < 0.05 ? "flat" : value > 0 ? "up" : "down";
    const arrow = tone === "up" ? "▲" : tone === "down" ? "▼" : "■";
    return <span className={`cvi-exec-delta is-${tone}`} title={title}><span aria-hidden="true">{arrow}</span>{fmtSigned(value, language)}</span>;
}

function StatePill({ state, c }) {
    return <span className={`cvi-exec-state is-${state}`}>
        <span className="cvi-exec-state-dot" aria-hidden="true" />{c.periodStates[state] || state}
    </span>;
}

function Card({ title, subtitle, aside, children, className = "", id }) {
    const titleId = id ? `${id}-title` : undefined;
    return <section className={`cvi-exec-card ${className}`} aria-labelledby={titleId} id={id}>
        {(title || aside) && <header className="cvi-exec-card-head">
            <div>
                {title && <h2 id={titleId}>{title}</h2>}
                {subtitle && <p>{subtitle}</p>}
            </div>
            {aside}
        </header>}
        {children}
    </section>;
}

function Skeleton({ lines = 3 }) {
    return <div className="cvi-exec-skeleton" aria-hidden="true">
        {Array.from({ length: lines }, (_, i) => <span key={i} style={{ width: `${92 - i * 14}%` }} />)}
    </div>;
}

// ── Hero: organization CVI and its two dimensions ───────────────────────────────────────

function DimensionTile({ label, help, value, previous, language, count, total, variant }) {
    const change = delta(value, previous);
    return <div className={`cvi-exec-dimension is-${variant}`}>
        <div className="cvi-exec-dimension-top">
            <span className="cvi-exec-dimension-mark" aria-hidden="true" />
            <strong>{label}</strong>
        </div>
        <div className="cvi-exec-dimension-value">
            <span className="cvi-exec-dimension-number">{fmt(value, language)}</span>
            <DeltaChip value={change} language={language} />
        </div>
        <div className="cvi-exec-meter" aria-hidden="true"><span style={{ width: `${value == null ? 0 : Math.max(0, Math.min(100, value))}%` }} /></div>
        <small>{help}{count != null && total ? ` · n = ${count}/${total}` : ""}</small>
    </div>;
}

function HeroCard({ c, language, summary, state, baseline, comparable = true, loading, failed, trend = [], selectedId }) {
    const h = c.hero;
    const change = baseline ? delta(summary?.cvi, baseline.cvi) : null;
    return <Card className="cvi-exec-hero" title={h.title} id="exec-hero"
        aside={<StatePill state={failed ? "error" : state} c={{ periodStates: { ...c.periodStates, error: c.banner.errorTitle } }} />}>
        {loading ? <Skeleton lines={5} /> : <>
            <div className="cvi-exec-hero-main">
                <div className="cvi-exec-hero-score">
                    <span className={`cvi-exec-hero-value${summary?.cvi == null ? " is-empty" : ""}`}>{summary?.cvi == null ? "—" : fmt(summary.cvi, language)}</span>
                    {summary?.cvi != null && <span className="cvi-exec-hero-scale">/ 100</span>}
                </div>
                <div className="cvi-exec-hero-meta">
                    {summary?.cvi == null
                        ? <><strong>{h.noScore}</strong><span>{failed ? c.banner.error : h.noScoreHelp}</span></>
                        : <>
                            <span>{h.caption}</span>
                            <span>{h.basis(summary.withCvi, summary.total)}</span>
                            {baseline
                                ? <span className="cvi-exec-hero-compare"><DeltaChip value={change} language={language} />{h.vs(localizePeriodLabel(baseline.label, language))}</span>
                                : <span className="cvi-exec-muted">{comparable ? h.noBaseline : h.comparisonWithheld}</span>}
                            {baseline && state !== "final" && <span className="cvi-exec-muted">{h.provisionalComparison}</span>}
                        </>}
                </div>
            </div>
            <div className="cvi-exec-dimensions">
                <DimensionTile variant="individual" label={h.individual} help={h.individualHelp} value={summary?.individual}
                    previous={baseline?.individual} language={language} count={summary?.individualCount} total={summary?.total} />
                <DimensionTile variant="collective" label={h.collective} help={h.collectiveHelp} value={summary?.collective}
                    previous={baseline?.collective} language={language} count={summary?.collectiveCount} total={summary?.total} />
            </div>
            {trend.length > 1 && <ol className="cvi-exec-cycles" aria-label={h.cycles}>
                {trend.map(item => <li key={item.periodId} className={`${item.state === "final" ? "is-final" : "is-provisional"}${String(item.periodId) === String(selectedId) ? " is-selected" : ""}`}>
                    <span>{localizePeriodLabel(item.label, language)}</span>
                    <strong>{item.failed ? "—" : fmt(item.cvi, language)}</strong>
                    <small>{item.failed ? c.trend.failed : item.state === "final" ? c.trend.final : c.trend.provisional}</small>
                </li>)}
            </ol>}
            <p className="cvi-exec-formula"><span aria-hidden="true">√</span>{h.formula}</p>
        </>}
    </Card>;
}

// ── Executive attention ─────────────────────────────────────────────────────────────────

const SEVERITY_ICON = { critical: "warning", warning: "flag", info: "bell" };

function AttentionCard({ c, items, loading, onTarget }) {
    const a = c.attention;
    return <Card className="cvi-exec-attention" title={a.title} subtitle={a.subtitle} id="exec-attention">
        {loading ? <Skeleton lines={4} /> : items.length === 0
            ? <div className="cvi-exec-empty is-success"><UiIcon name="check" size={18} /><span>{a.empty}</span></div>
            : <ul className="cvi-exec-attention-list">
                {items.map(item => {
                    const text = a.items[item.kind](item);
                    const actionable = Boolean(item.target && onTarget);
                    const body = <>
                        <span className={`cvi-exec-severity is-${item.severity}`}><UiIcon name={SEVERITY_ICON[item.severity]} size={14} /></span>
                        <span className="cvi-exec-attention-text">
                            <span className={`cvi-exec-severity-label is-${item.severity}`}>{a.severities[item.severity]}</span>
                            <strong>{text.title}</strong>
                            {text.detail && <small>{text.detail}</small>}
                        </span>
                        {actionable && <UiIcon name="chevronRight" size={14} className="cvi-exec-chevron" />}
                    </>;
                    return <li key={item.id}>
                        {actionable
                            ? <button type="button" className="cvi-exec-attention-item" onClick={() => onTarget(item)}>{body}</button>
                            : <div className="cvi-exec-attention-item">{body}</div>}
                    </li>;
                })}
            </ul>}
    </Card>;
}

// ── Trend chart ─────────────────────────────────────────────────────────────────────────

const SERIES = [
    { key: "cvi", shape: "circle" },
    { key: "individual", shape: "square" },
    { key: "collective", shape: "diamond" },
];

function Marker({ shape, x, y, hollow, className }) {
    const props = { className: `${className}${hollow ? " is-hollow" : ""}` };
    if (shape === "square") return <rect {...props} x={x - 4} y={y - 4} width={8} height={8} rx={1.5} />;
    if (shape === "diamond") return <path {...props} d={`M${x} ${y - 5.5}L${x + 5.5} ${y}L${x} ${y + 5.5}L${x - 5.5} ${y}Z`} />;
    return <circle {...props} cx={x} cy={y} r={4.5} />;
}

// Width of an element, so the chart is drawn at its real size (text stays legible on phones
// instead of being scaled down with the SVG).
function useElementWidth(fallback) {
    const [node, setNode] = useState(null);
    const [width, setWidth] = useState(fallback);
    useEffect(() => {
        if (!node) return undefined;
        const measure = () => { if (node.clientWidth) setWidth(node.clientWidth); };
        measure();
        if (typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, [node]);
    return [setNode, width];
}

export function TrendChart({ trend, language, c, selectedId, onSelect }) {
    const t = c.trend;
    const [hover, setHover] = useState(null);
    const [plotRef, plotWidth] = useElementWidth(640);
    const values = trend.flatMap(item => SERIES.map(series => item[series.key])).filter(value => value != null);
    if (!values.length) return <div className="cvi-exec-empty">{t.empty}</div>;
    const W = Math.max(280, Math.round(plotWidth)), H = W < 480 ? 200 : 230, padL = 34, padR = 22, padT = 18, padB = 34;
    const min = Math.min(...values);
    const lower = Math.max(0, Math.floor((min - 5) / 10) * 10);
    const upper = 100;
    const ticks = [];
    for (let v = lower; v <= upper; v += (upper - lower) > 50 ? 20 : 10) ticks.push(v);
    const step = trend.length > 1 ? (W - padL - padR) / (trend.length - 1) : 0;
    const xOf = i => (trend.length > 1 ? padL + i * step : (W + padL - padR) / 2);
    const yOf = v => padT + (1 - (v - lower) / (upper - lower)) * (H - padT - padB);
    const segments = key => {
        const out = [];
        for (let i = 1; i < trend.length; i += 1) {
            const a = trend[i - 1][key], b = trend[i][key];
            if (a == null || b == null) continue;
            out.push({ i, d: `M${xOf(i - 1)} ${yOf(a)}L${xOf(i)} ${yOf(b)}`, provisional: trend[i].state !== "final" || trend[i - 1].state !== "final" });
        }
        return out;
    };
    const active = hover != null ? trend[hover] : null;
    const label = item => localizePeriodLabel(item.label, language);
    return <div className="cvi-exec-trend">
        <div className="cvi-exec-legend" aria-hidden="true">
            {SERIES.map(series => <span key={series.key} className={`is-${series.key}`}>
                <svg width="22" height="12" viewBox="0 0 22 12"><path d="M1 6H21" className={`cvi-exec-line is-${series.key}`} /><Marker shape={series.shape} x={11} y={6} className={`cvi-exec-marker is-${series.key}`} /></svg>
                {t.series[series.key]}
            </span>)}
            <span className="is-provisional"><svg width="22" height="12" viewBox="0 0 22 12"><path d="M1 6H21" className="cvi-exec-line is-neutral is-provisional" /></svg>{t.provisional}</span>
        </div>
        <div className="cvi-exec-trend-plot" ref={plotRef}>
            <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${t.title}: ${trend.map(item => `${label(item)} ${t.series.cvi} ${fmt(item.cvi, language)}`).join(", ")}`}>
                {ticks.map(v => <g key={v} className="cvi-exec-grid">
                    <line x1={padL} x2={W - padR} y1={yOf(v)} y2={yOf(v)} />
                    <text x={padL - 8} y={yOf(v) + 3.5} textAnchor="end">{v}</text>
                </g>)}
                {trend.map((item, i) => <g key={item.periodId}>
                    {String(item.periodId) === String(selectedId) && <rect className="cvi-exec-selected-band" x={xOf(i) - 26} y={padT - 6} width={52} height={H - padT - padB + 12} rx={8} />}
                    <text className={`cvi-exec-axis-label${String(item.periodId) === String(selectedId) ? " is-selected" : ""}`} x={xOf(i)} y={H - 12} textAnchor="middle">{label(item)}</text>
                </g>)}
                {SERIES.map(series => segments(series.key).map(seg =>
                    <path key={`${series.key}-${seg.i}`} d={seg.d} className={`cvi-exec-line is-${series.key}${seg.provisional ? " is-provisional" : ""}`} />))}
                {SERIES.map(series => trend.map((item, i) => item[series.key] == null ? null :
                    <Marker key={`${series.key}-${item.periodId}`} shape={series.shape} x={xOf(i)} y={yOf(item[series.key])}
                        hollow={item.state !== "final"} className={`cvi-exec-marker is-${series.key}`} />))}
                {trend.map((item, i) => item.cvi != null && (i === trend.length - 1 || String(item.periodId) === String(selectedId)) &&
                    <text key={`lbl-${item.periodId}`} className="cvi-exec-point-label" x={xOf(i)} y={yOf(item.cvi) - 11} textAnchor="middle">{fmt(item.cvi, language)}</text>)}
            </svg>
            <div className="cvi-exec-trend-hits">
                {trend.map((item, i) => <button key={item.periodId} type="button"
                    className={String(item.periodId) === String(selectedId) ? "is-selected" : ""}
                    style={{ left: `${(xOf(i) / W) * 100}%` }}
                    onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
                    onFocus={() => setHover(i)} onBlur={() => setHover(null)}
                    onClick={() => onSelect?.(item.periodId)}
                    aria-label={`${label(item)} · ${item.state === "final" ? t.final : t.provisional} · ${t.series.cvi} ${fmt(item.cvi, language)} · ${t.series.individual} ${fmt(item.individual, language)} · ${t.series.collective} ${fmt(item.collective, language)}`} />)}
            </div>
            {active && <div className={`cvi-exec-tooltip${hover === 0 ? " is-start" : hover === trend.length - 1 ? " is-end" : ""}`} role="presentation" style={{ left: `${(xOf(hover) / W) * 100}%` }}>
                <strong>{label(active)} <em>{active.failed ? t.failed : active.state === "final" ? t.final : t.provisional}</em></strong>
                {SERIES.map(series => <span key={series.key}><i className={`is-${series.key}`} />{t.series[series.key]}<b>{fmt(active[series.key], language)}</b></span>)}
                <small>{t.people}: {active.withCvi}/{active.total}</small>
            </div>}
        </div>
        <div className="cvi-exec-sr-only"><table>
            <caption>{t.table}</caption>
            <thead><tr><th scope="col">{t.period}</th><th scope="col">{t.series.cvi}</th><th scope="col">{t.series.individual}</th><th scope="col">{t.series.collective}</th><th scope="col">{t.people}</th></tr></thead>
            <tbody>{trend.map(item => <tr key={item.periodId}>
                <th scope="row">{label(item)} ({item.failed ? t.failed : item.state === "final" ? t.final : t.provisional})</th>
                <td>{fmt(item.cvi, language)}</td><td>{fmt(item.individual, language)}</td><td>{fmt(item.collective, language)}</td>
                <td>{item.withCvi}/{item.total}</td>
            </tr>)}</tbody>
        </table></div>
    </div>;
}

// ── Coverage and distribution ───────────────────────────────────────────────────────────

const STATUS_ORDER = ["final", "provisional", "partial", "not_assessed"];

function CoverageCard({ c, language, summary, distribution, state, loading }) {
    const k = c.coverage;
    const total = summary?.total || 0;
    return <Card className="cvi-exec-coverage" title={k.title} subtitle={k.subtitle} id="exec-coverage">
        {loading ? <Skeleton lines={5} /> : <>
            <div className="cvi-exec-stack" role="img" aria-label={STATUS_ORDER.map(key => `${k.statuses[key]} ${summary?.counts?.[key] || 0}`).join(", ")}>
                {STATUS_ORDER.map(key => {
                    const count = summary?.counts?.[key] || 0;
                    return count ? <span key={key} className={`is-${key}`} style={{ flexGrow: count }} /> : null;
                })}
                {!total && <span className="is-not_assessed" style={{ flexGrow: 1 }} />}
            </div>
            <ul className="cvi-exec-stack-legend">
                {STATUS_ORDER.filter(key => key !== (state === "final" ? "provisional" : "final") && (key !== "partial" || state !== "final")).map(key =>
                    <li key={key}><i className={`is-${key}`} aria-hidden="true" /><span>{k.statuses[key]}</span><strong>{summary?.counts?.[key] || 0}</strong></li>)}
            </ul>
            {state !== "final" && <div className="cvi-exec-coverage-metric">
                <div><span>{k.assessment}</span><strong>{summary?.coverage == null ? "—" : withPercent(fmt(summary.coverage, language, 0), language)}</strong></div>
                <small>{k.assessmentHelp}</small>
            </div>}
            <div className="cvi-exec-distribution">
                <span className="cvi-exec-subhead">{k.distribution}</span>
                {distribution.total === 0 ? <small className="cvi-exec-muted">{k.none}</small> :
                    <div className="cvi-exec-bands">
                        {["high", "acceptable", "attention"].map(band => <div key={band} className={`is-${band}`}>
                            <strong>{distribution[band]}</strong><span>{k.bands[band]}</span>
                        </div>)}
                    </div>}
            </div>
        </>}
    </Card>;
}

// ── Component breakdown ─────────────────────────────────────────────────────────────────

function ComponentRow({ label, stat, language, b }) {
    const value = stat?.value ?? null;
    return <li className={value == null ? "is-empty" : ""}>
        <div className="cvi-exec-component-label"><span>{label}</span><small>{value == null ? b.notAssessed : b.n(stat.count)}</small></div>
        <div className="cvi-exec-component-bar" aria-hidden="true"><span style={{ width: `${value == null ? 0 : Math.max(0, Math.min(100, value))}%` }} /></div>
        <strong>{fmt(value, language)}</strong>
    </li>;
}

function BreakdownCard({ c, language, summary, loading }) {
    const b = c.breakdown;
    const comp = summary?.components || {};
    return <Card className="cvi-exec-breakdown" title={b.title} subtitle={b.subtitle} id="exec-breakdown">
        {loading ? <Skeleton lines={6} /> : <div className="cvi-exec-breakdown-grid">
            <div className="is-individual">
                <div className="cvi-exec-breakdown-head"><span className="cvi-exec-dimension-mark" aria-hidden="true" /><strong>{b.individual}</strong><b>{fmt(summary?.individual, language)}</b></div>
                <ul>{["routine", "nonRoutine", "individualGoals", "peerFeedback"].map(key =>
                    <ComponentRow key={key} label={b.components[key]} stat={comp[key]} language={language} b={b} />)}</ul>
            </div>
            <div className="is-collective">
                <div className="cvi-exec-breakdown-head"><span className="cvi-exec-dimension-mark" aria-hidden="true" /><strong>{b.collective}</strong><b>{fmt(summary?.collective, language)}</b></div>
                <ul>{["team", "upstream", "organization"].map(key =>
                    <ComponentRow key={key} label={b.components[key]} stat={comp[key]} language={language} b={b} />)}</ul>
            </div>
        </div>}
    </Card>;
}

// ── Business units ──────────────────────────────────────────────────────────────────────

function UnitsCard({ c, language, units, loading, onOpenUnit }) {
    const u = c.units;
    return <Card className="cvi-exec-units" title={u.title} subtitle={u.subtitle} id="exec-units">
        {loading ? <Skeleton lines={5} /> : units.length === 0 ? <div className="cvi-exec-empty">{u.empty}</div> : <>
            <table className="cvi-exec-table">
                <thead><tr>
                    <th scope="col">{u.unit}</th>
                    <th scope="col" className="is-num is-wide-only">{u.people}</th>
                    <th scope="col" className="is-bar">{u.cvi}</th>
                    <th scope="col" className="is-num is-wide-only">{u.individual}</th>
                    <th scope="col" className="is-num is-wide-only">{u.collective}</th>
                    <th scope="col" className="is-num">{u.change}</th>
                </tr></thead>
                <tbody>{units.map(unit => <tr key={unit.id}>
                    <th scope="row">
                        {unit.drillable && onOpenUnit
                            ? <button type="button" className="cvi-exec-unit-link" onClick={() => onOpenUnit(unit.id)} aria-label={u.open(unit.label)}>
                                <strong>{unit.label}</strong><UiIcon name="chevronRight" size={13} className="cvi-exec-chevron" /></button>
                            : <strong>{unit.label}</strong>}
                        <small>{u.coverage(unit.withCvi, unit.people)}{unit.childCount ? ` · ${u.subunits(unit.childCount)}` : ""}</small>
                    </th>
                    <td className="is-num is-wide-only">{unit.people}</td>
                    <td className="is-bar">
                        <div className="cvi-exec-unit-bar">
                            <span className="cvi-exec-unit-track" aria-hidden="true"><span style={{ width: `${unit.cvi == null ? 0 : Math.max(0, Math.min(100, unit.cvi))}%` }} className={unit.comparable ? "" : "is-thin"} /></span>
                            <strong>{fmt(unit.cvi, language)}</strong>
                        </div>
                        {!unit.comparable && unit.cvi != null && <small className="cvi-exec-muted">{u.smallSample}</small>}
                    </td>
                    <td className="is-num is-wide-only">{fmt(unit.individual, language)}</td>
                    <td className="is-num is-wide-only">{fmt(unit.collective, language)}</td>
                    <td className="is-num">{unit.change == null ? <span className="cvi-exec-muted">—</span> : <DeltaChip value={unit.change} language={language} />}</td>
                </tr>)}</tbody>
            </table>
            <p className="cvi-exec-note">{u.note}</p>
        </>}
    </Card>;
}

// ── Strategic indicators ────────────────────────────────────────────────────────────────

const INDICATOR_LIMIT = 6;

function IndicatorsCard({ c, language, indicators, loading }) {
    const k = c.indicators;
    const [expanded, setExpanded] = useState(false);
    const ranked = useMemo(() => rankIndicators(indicators), [indicators]);
    const counts = useMemo(() => indicatorCounts(indicators), [indicators]);
    const visible = expanded ? ranked : ranked.slice(0, INDICATOR_LIMIT);
    return <Card className="cvi-exec-indicators" title={k.title} subtitle={k.subtitle} id="exec-indicators"
        aside={!loading && counts.total > 0 && <div className="cvi-exec-indicator-summary">
            {["critical", "watch", "on_track", "no_data"].filter(key => counts[key]).map(key =>
                <span key={key} className={`cvi-exec-tag is-${key}`}>{counts[key]} · {k.statuses[key]}</span>)}
        </div>}>
        {loading ? <Skeleton lines={5} /> : ranked.length === 0 ? <div className="cvi-exec-empty">{k.empty}</div> : <>
            <ul className="cvi-exec-indicator-list">
                {visible.map(item => <li key={item.indicatorId} className={`is-${item.status}`}>
                    <div className="cvi-exec-indicator-main">
                        <strong>{item.name}</strong>
                        <small>{k.scopes[item.scope] || item.scope} · {k.actual} {item.actual == null ? "—" : formatNumber(item.actual, language)}{item.unit ? ` ${item.unit}` : ""} · {k.target} {item.target == null ? "—" : formatNumber(item.target, language)}{item.unit ? ` ${item.unit}` : ""}</small>
                    </div>
                    <div className="cvi-exec-indicator-value">
                        <div className="cvi-exec-indicator-track" aria-hidden="true">
                            <span style={{ width: `${item.achievement == null ? 0 : Math.max(0, Math.min(100, item.achievement))}%` }} />
                            <i style={{ left: "65%" }} /><i style={{ left: "85%" }} />
                        </div>
                        <strong>{item.achievement == null ? "—" : withPercent(fmt(item.achievement, language, 0), language)}</strong>
                    </div>
                    <span className={`cvi-exec-tag is-${item.status}`}>{k.statuses[item.status]}</span>
                </li>)}
            </ul>
            {ranked.length > INDICATOR_LIMIT && <button type="button" className="cvi-exec-link" onClick={() => setExpanded(value => !value)} aria-expanded={expanded}>
                {expanded ? k.showLess : k.showAll(ranked.length)}</button>}
        </>}
    </Card>;
}

// ── Page ────────────────────────────────────────────────────────────────────────────────

export default function ExecutiveDashboard({
    language = "en", periods = [], activePeriodId = null, orgUnits = [], departments = [],
    pendingDecisions = 0, onOpenUnit, onOpenInbox, onOpenHistory, onOpenMembers, users = [], organizationName = "",
}) {
    const c = executiveCopy(language);
    const started = useMemo(() => periods.filter(period => periodState(period) !== "draft"), [periods]);
    const { byPeriod, reload } = useExecutivePeriods(started);
    const [selectedId, setSelectedId] = useState(activePeriodId || started[started.length - 1]?.id || null);
    useEffect(() => {
        if (!selectedId || !started.some(period => String(period.id) === String(selectedId))) {
            setSelectedId(activePeriodId || started[started.length - 1]?.id || null);
        }
    }, [activePeriodId, started, selectedId]);

    const trend = useMemo(() => buildExecutiveTrend(started.map(period => ({
        period, payload: byPeriod[period.id]?.payload || null, error: byPeriod[period.id]?.error || null,
    }))), [started, byPeriod]);

    const selectedPeriod = started.find(period => String(period.id) === String(selectedId)) || null;
    const current = selectedId ? byPeriod[selectedId] : null;
    const payload = current?.payload || null;
    const loading = Boolean(selectedId) && (!current || current.loading) && !payload;
    const failed = Boolean(current?.error);
    const state = periodState(payload?.period || selectedPeriod);
    const people = useMemo(() => payload?.people || [], [payload]);
    const summary = useMemo(() => (payload ? summarizePeople(people) : null), [payload, people]);
    const distribution = useMemo(() => performanceDistribution(people), [people]);
    const comparable = comparisonReliable(state, summary);
    // Comparisons with an earlier finalized period are withheld while a provisional period is
    // too incomplete to compare (see comparisonReliable).
    const baseline = comparable ? previousFinalized(trend, selectedId) : null;
    const baselinePeople = baseline ? byPeriod[baseline.periodId]?.payload?.people || null : null;
    const unitDefs = useMemo(() => topLevelUnits({ orgUnits, departments }), [orgUnits, departments]);
    const units = useMemo(() => unitBreakdown({ units: unitDefs, people, previousPeople: baselinePeople }), [unitDefs, people, baselinePeople]);
    const indicators = useMemo(() => payload?.indicators || [], [payload]);
    const attention = useMemo(() => (loading ? [] : buildAttentionItems({
        state, summary, units, indicators, failed,
        orgChange: baseline ? delta(summary?.cvi, baseline.cvi) : null,
        pendingDecisions, comparable,
    })), [loading, state, summary, units, indicators, failed, baseline, pendingDecisions, comparable]);
    const formulaVersion = people.find(person => person.formulaVersion)?.formulaVersion || null;
    const forbidden = current?.error?.status === 403;

    const onTarget = item => {
        if (item.target === "inbox") return onOpenInbox?.();
        if (item.target === "units" && item.unitId && onOpenUnit && unitDefs.find(unit => unit.id === item.unitId)?.drillable) return onOpenUnit(item.unitId);
        const anchor = { indicators: "exec-indicators", units: "exec-units", coverage: "exec-coverage", trend: "exec-trend" }[item.target];
        const element = anchor && typeof document !== "undefined" ? document.getElementById(anchor) : null;
        if (element) {
            element.scrollIntoView?.({ behavior: "smooth", block: "start" });
            element.focus?.({ preventScroll: true });
        }
    };

    return <div className="cvi-dashboard-page cvi-exec-page" {...MANAGED} lang={language}>
        <header className="cvi-exec-masthead">
            <div className="cvi-exec-masthead-copy">
                <span className="cvi-exec-eyebrow">{c.eyebrow}</span>
                <h1>{c.title}</h1>
                <p>{c.subtitle(payload?.population ?? "—", unitDefs.length)}</p>
            </div>
            <div className="cvi-exec-masthead-actions">
                <label className="cvi-exec-period">
                    <span>{c.periodLabel}</span>
                    <select value={selectedId || ""} onChange={event => setSelectedId(event.target.value)} disabled={!started.length}>
                        {[...trend].reverse().map(item => <option key={item.periodId} value={item.periodId}>
                            {localizePeriodLabel(item.label, language)} · {c.periodStates[item.state]}
                        </option>)}
                    </select>
                    <UiIcon name="chevronDown" size={14} className="cvi-exec-period-caret" />
                </label>
                <div className="cvi-exec-quick">
                    {onOpenInbox && <button type="button" className="cvi-exec-button is-primary" onClick={onOpenInbox}>
                        <UiIcon name="inbox" size={15} />{c.actions.inbox}{pendingDecisions > 0 && <span className="cvi-exec-count">{pendingDecisions}</span>}</button>}
                    {onOpenHistory && <button type="button" className="cvi-exec-button" onClick={onOpenHistory}><UiIcon name="history" size={15} />{c.actions.history}</button>}
                    {onOpenMembers && <button type="button" className="cvi-exec-button" onClick={onOpenMembers}><UiIcon name="members" size={15} />{c.actions.members}</button>}
                </div>
            </div>
        </header>

        {failed ? <div className="cvi-exec-banner is-error" role="alert">
            <UiIcon name="warning" size={18} />
            <div><strong>{c.banner.errorTitle}</strong><span>{forbidden ? c.banner.forbidden : c.banner.error}</span></div>
            {!forbidden && <button type="button" className="cvi-exec-button" onClick={reload}>{c.banner.retry}</button>}
        </div> : loading ? <div className="cvi-exec-banner is-loading" role="status"><span className="cvi-exec-spinner" aria-hidden="true" /><div><span>{c.banner.loading}</span></div></div>
            : state === "final" ? <div className="cvi-exec-banner is-final" role="status">
                <UiIcon name="shield" size={18} />
                <div><strong>{c.banner.finalTitle}</strong><span>{c.banner.final(formulaVersion)}</span></div>
            </div> : <div className="cvi-exec-banner is-provisional" role="status">
                <UiIcon name="schedule" size={18} />
                <div><strong>{c.banner.provisionalTitle}</strong><span>{c.banner.provisional(summary?.withCvi ?? 0, summary?.total ?? 0)}</span></div>
            </div>}

        <div className="cvi-exec-row is-hero">
            <HeroCard c={c} language={language} summary={summary} state={state} baseline={baseline} comparable={comparable} loading={loading} failed={failed} trend={trend} selectedId={selectedId} />
            <AttentionCard c={c} items={attention} loading={loading} onTarget={onTarget} />
        </div>

        <ExecutiveOrgStructure c={c} language={language} orgUnits={orgUnits} people={people} users={users}
            indicators={indicators} organizationName={organizationName} period={selectedPeriod} state={state}
            loading={loading} onOpenUnit={onOpenUnit} />

        <div className="cvi-exec-row is-trend">
            <Card className="cvi-exec-trend-card" title={c.trend.title} subtitle={c.trend.subtitle} id="exec-trend"
                aside={onOpenHistory && <button type="button" className="cvi-exec-link" onClick={onOpenHistory}>{c.trend.openHistory}</button>}>
                {trend.every(item => !item.loaded) ? <Skeleton lines={5} /> : <TrendChart trend={trend} language={language} c={c} selectedId={selectedId} onSelect={setSelectedId} />}
            </Card>
            <CoverageCard c={c} language={language} summary={summary} distribution={distribution} state={state} loading={loading} />
        </div>

        <BreakdownCard c={c} language={language} summary={summary} loading={loading} />

        <div className="cvi-exec-row is-detail">
            <UnitsCard c={c} language={language} units={units} loading={loading} onOpenUnit={onOpenUnit} />
            <IndicatorsCard c={c} language={language} indicators={indicators} loading={loading} />
        </div>

        <p className="cvi-exec-footnote">{c.footnote}</p>
    </div>;
}
